import { supabase } from '../lib/supabase';
import { SellerStoreIntegration, SellerRevenueMonthly, LeaderboardSeller, REVENUE_MILESTONES, StoreBreakdownItem } from '../types/sellerIntegration';

export function getCurrentYearMonth(): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Bogota', year: 'numeric', month: '2-digit' }).formatToParts(new Date());
  return `${parts.find(p => p.type === 'year')!.value}-${parts.find(p => p.type === 'month')!.value}`;
}

export function formatMonthName(yearMonthStr: string): string {
  const [year, month] = yearMonthStr.split('-');
  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
  const monthName = date.toLocaleDateString('pt-BR', { month: 'long' });
  return `${monthName.charAt(0).toUpperCase() + monthName.slice(1)} de ${year}`;
}

async function requestSync(body: Record<string, unknown>): Promise<{ success: boolean; data?: any; error?: string }> {
  try {
    const { data } = await supabase.auth.getSession();
    if (!data.session) throw new Error('Faça login novamente.');
    const response = await fetch('/.netlify/functions/mastershop-sync', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data.session.access_token}` },
      body: JSON.stringify(body), signal: AbortSignal.timeout(20000),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Falha ao solicitar sincronização.');
    return { success: true, data: result.data };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Falha de conexão.' };
  }
}

export const mastershopHelpers = {
  /**
   * Obtém todas as lojas cadastradas do usuário
   */
  getUserStores: async (userId: string): Promise<{ data: SellerStoreIntegration[]; error: any }> => {
    try {
      const { data, error } = await supabase
        .from('seller_store_integrations')
        .select('*')
        .eq('user_id', userId)
        .eq('platform', 'mastershop')
        .order('created_at', { ascending: true });

      if (error) {
        if (error.code !== '42P01' && error.code !== 'PGRST116' && error.code !== 'PGRST204') {
          console.warn('Aviso ao buscar lojas Master Shop:', error.message || error);
        }
        return { data: [], error };
      }

      return { data: (data || []).map(store => {
        const rawKey = store.api_key || '';
        const masked = rawKey ? (rawKey.length > 11 ? `${rawKey.slice(0, 7)}...${rawKey.slice(-4)}` : '••••••••••••') : '';
        return {
          ...store,
          api_key: masked,
          last_sync_revenue_brl: store.last_sync_month === getCurrentYearMonth() ? store.last_sync_revenue_brl : 0,
          last_sync_revenue_cop: store.last_sync_month === getCurrentYearMonth() ? store.last_sync_revenue_cop : 0,
          last_sync_orders: store.last_sync_month === getCurrentYearMonth() ? store.last_sync_orders : 0,
        };
      }) as SellerStoreIntegration[], error: null };
    } catch (err: any) {
      console.error('Erro inesperado ao buscar lojas:', err);
      return { data: [], error: err };
    }
  },

  /**
   * Compatibilidade retroativa: obtém a primeira loja ou null
   */
  getIntegration: async (userId: string): Promise<{ data: SellerStoreIntegration | null; error: any }> => {
    const res = await mastershopHelpers.getUserStores(userId);
    return { data: res.data?.[0] || null, error: res.error };
  },

  /**
   * Adiciona ou atualiza uma loja com nome e ApiKey
   */
  saveApiKey: async (
    userId: string, 
    apiKey: string, 
    storeName?: string, 
    storeId?: string
  ): Promise<{ success: boolean; data?: SellerStoreIntegration; error?: string }> => {
    try {
      const cleanKey = (apiKey || '').trim();
      if (!storeId && !cleanKey) {
        return { success: false, error: 'A chave de API não pode estar vazia.' };
      }

      // Se passou storeId, atualiza a existente
      if (storeId) {
        const updatePayload: Record<string, any> = {
          store_name: storeName?.trim() || 'Loja Master',
          is_active: true,
          updated_at: new Date().toISOString()
        };

        // Atualiza a api_key somente se uma nova chave válida e não mascarada for informada
        if (cleanKey && !cleanKey.includes('•') && !cleanKey.includes('...')) {
          updatePayload.api_key = cleanKey;
        }

        const { data, error } = await supabase
          .from('seller_store_integrations')
          .update(updatePayload)
          .eq('id', storeId)
          .eq('user_id', userId)
          .select()
          .single();

        if (error) {
          return { success: false, error: error.message };
        }
        return { success: true, data: data as SellerStoreIntegration };
      }

      // Se não passou storeId, insere uma nova loja
      const existingStores = await mastershopHelpers.getUserStores(userId);
      const defaultName = storeName?.trim() || `Loja ${(existingStores.data?.length || 0) + 1}`;

      const { data, error } = await supabase
        .from('seller_store_integrations')
        .upsert({
          user_id: userId,
          platform: 'mastershop',
          api_key: cleanKey,
          store_name: defaultName,
          is_active: true,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,api_key'
        })
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: data as SellerStoreIntegration };
    } catch (err: any) {
      console.error('Erro inesperado ao salvar loja:', err);
      return { success: false, error: err.message || 'Erro inesperado ao salvar loja.' };
    }
  },

  /**
   * Exclui / Desconecta uma loja específica pelo ID
   */
  deleteStore: async (storeId: string, userId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase
        .from('seller_store_integrations')
        .delete()
        .eq('id', storeId)
        .eq('user_id', userId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Erro ao excluir loja.' };
    }
  },

  /**
   * Desconecta todas as lojas do usuário
   */
  disconnect: async (userId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase
        .from('seller_store_integrations')
        .delete()
        .eq('user_id', userId)
        .eq('platform', 'mastershop');

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Erro ao desconectar integração.' };
    }
  },

  /**
   * Dispara a sincronização de faturamento (todas as lojas do usuário consolidadas)
   */
  syncRevenue: async (userId: string, targetMonth = getCurrentYearMonth(), _apiKey?: string) => {
    return requestSync({ userId, targetMonth });
  },

  /**
   * Busca o faturamento mensal consolidado do vendedor no banco de dados
   */
  getMonthlyRevenue: async (userId: string, targetMonth = getCurrentYearMonth()): Promise<{ data: SellerRevenueMonthly | null; error: any }> => {
    try {
      const { data, error } = await supabase
        .from('seller_revenue_monthly')
        .select('*')
        .eq('user_id', userId)
        .eq('year_month', targetMonth)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        if (error.code !== '42P01') {
          console.warn('Aviso de faturamento mensal:', error.message || error);
        }
        return { data: null, error };
      }

      // Se não existir registro no banco, retorna objeto padrão com meta de 100k
      if (!data) {
        return {
          data: {
            id: '',
            user_id: userId,
            year_month: targetMonth,
            total_revenue: 0,
            revenue_cop: 0,
            currency: 'BRL',
            total_orders: 0,
            store_count: 0,
            store_breakdown: [],
            current_milestone_target: 100000,
            claimed_milestones: [],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
          error: null,
        };
      }

      return { data: data as SellerRevenueMonthly, error: null };
    } catch (err: any) {
      return { data: null, error: err };
    }
  },

  /**
   * Reivindica o marco da meta (100k, 250k, 500k, etc.) e escala a régua
   */
  claimMilestone: async (_userId: string, milestoneId: string, targetMonth = getCurrentYearMonth()): Promise<{ success: boolean; nextTarget?: number; error?: string }> => {
    const { data, error } = await supabase.rpc('claim_seller_milestone', { target_month: targetMonth, milestone: milestoneId });
    return error ? { success: false, error: error.message } : { success: true, nextTarget: Number(data) };
  },

  /**
   * Busca o ranking/leaderboard de vendedores do mês (soma consolidada com breakdown de lojas)
   */
  getLeaderboard: async (targetMonth = getCurrentYearMonth()): Promise<{ data: LeaderboardSeller[]; error: any }> => {
    try {
      // 1. Tentar executar a RPC get_seller_leaderboard
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_seller_leaderboard', {
        target_month: targetMonth
      });

      if (!rpcError && Array.isArray(rpcData)) {
        return { data: rpcData as LeaderboardSeller[], error: null };
      }

      // 2. Fallback com join simples via client Supabase
      const { data, error } = await supabase
        .from('seller_revenue_monthly')
        .select(`
          user_id,
          total_revenue,
          revenue_cop,
          currency,
          total_orders,
          store_count,
          store_breakdown,
          current_milestone_target,
          claimed_milestones,
          updated_at,
          user_profiles:user_id (
            full_name,
            avatar_url,
            email
          )
        `)
        .eq('year_month', targetMonth)
        .gt('total_revenue', 0)
        .order('total_revenue', { ascending: false })
        .limit(50);

      if (error) {
        if (error.code !== '42P01') {
          console.warn('Aviso ao buscar leaderboard:', error.message || error);
        }
        return { data: [], error };
      }

      const formatted: LeaderboardSeller[] = (data || []).map((item: any, index: number) => ({
        rank: index + 1,
        user_id: item.user_id,
        full_name: item.user_profiles?.full_name || 'Vendedor Master',
        avatar_url: item.user_profiles?.avatar_url,
        email: item.user_profiles?.email || '',
        total_revenue: Number(item.total_revenue || 0),
        revenue_cop: Number(item.revenue_cop || 0),
        currency: item.currency || 'BRL',
        total_orders: Number(item.total_orders || 0),
        store_count: Number(item.store_count || 1),
        store_breakdown: item.store_breakdown || [],
        current_milestone_target: Number(item.current_milestone_target || 100000),
        claimed_milestones: item.claimed_milestones || [],
        updated_at: item.updated_at,
      }));

      return { data: formatted, error: null };
    } catch (err: any) {
      console.error('Erro inesperado ao buscar leaderboard:', err);
      return { data: [], error: err };
    }
  },

  /**
   * Dispara a sincronização de todos os vendedores ativos (Admin)
   */
  syncAllSellers: async (targetMonth = getCurrentYearMonth()) => requestSync({ targetMonth }),
  syncAllSellersBackground: async (targetMonth = getCurrentYearMonth()) => requestSync({ targetMonth }),
  getSyncStatus: async (targetMonth: string, userId?: string) => requestSync({ status: true, targetMonth, userId }),

  /**
   * Busca lista agrupada de vendedores com suas lojas e faturamentos (Admin)
   */
  getAdminIntegrationsList: async (targetMonth = getCurrentYearMonth()): Promise<{ data: any[]; error: any }> => {
    try {
      // 1. Buscar todas as lojas cadastradas
      const stores: any[] = [];
      for (let offset = 0; ; offset += 500) {
        const { data: page, error } = await supabase
        .from('seller_store_integrations')
        .select(`
          id,
          user_id,
          platform,
          store_name,
          api_key,
          is_active,
          last_sync_at,
          last_sync_status,
          last_error_message,
          last_sync_revenue_brl,
          last_sync_revenue_cop,
          last_sync_orders,
          last_sync_month,
          created_at,
          updated_at,
          user_profiles:user_id (
            id,
            full_name,
            avatar_url,
            email,
            role
          )
        `)
        .order('id', { ascending: true }).range(offset, offset + 499);
        if (error) throw error;
        stores.push(...(page || []));
        if (!page || page.length < 500) break;
      }

      // 2. Agrupar lojas por user_id
      const userStoresMap: Record<string, any[]> = {};
      const userProfilesMap: Record<string, any> = {};

      (stores || []).forEach((store: any) => {
        if (!userStoresMap[store.user_id]) {
          userStoresMap[store.user_id] = [];
        }
        userStoresMap[store.user_id].push(store);
        if (store.user_profiles) {
          userProfilesMap[store.user_id] = store.user_profiles;
        }
      });

      const userIds = Object.keys(userStoresMap);

      // 3. Buscar faturamento consolidado de cada usuário
      const revenueMap: Record<string, any> = {};
      for (let offset = 0; offset < userIds.length; offset += 100) {
        const { data: revList, error: revenueError } = await supabase
          .from('seller_revenue_monthly')
          .select('*')
          .in('user_id', userIds.slice(offset, offset + 100))
          .eq('year_month', targetMonth);

        if (revenueError) throw revenueError;
        (revList || []).forEach((r: any) => {
          revenueMap[r.user_id] = r;
        });
      }

      // 4. Montar lista de vendedores com lista de lojas
      const result = userIds.map(uid => {
        const uStores = userStoresMap[uid] || [];
        const rev = revenueMap[uid] || {
          total_revenue: 0, revenue_cop: 0, total_orders: 0, currency: 'BRL',
          current_milestone_target: 100000, claimed_milestones: [],
          store_count: uStores.length, store_breakdown: [],
        };

        const hasError = uStores.some(s => s.last_sync_status === 'error');
        const allSuccess = Boolean(revenueMap[uid]) && uStores.length > 0 && uStores.every(s => s.last_sync_status === 'success');
        const overallStatus = hasError ? 'error' : allSuccess ? 'success' : 'pending';

        return {
          user_id: uid,
          user: userProfilesMap[uid] || {
            full_name: 'Vendedor Master',
            email: 'sem email',
            avatar_url: null,
            role: 'user'
          },
          stores: uStores.map(store => {
            const item = rev.store_breakdown?.find((b: any) => b.store_id === store.id);
            return { ...store, last_sync_revenue_brl: item?.revenue_brl ?? 0,
              last_sync_revenue_cop: item?.revenue_cop ?? 0, last_sync_orders: item?.orders ?? 0 };
          }),
          store_count: uStores.length,
          last_sync_status: overallStatus,
          last_sync_at: uStores[0]?.last_sync_at,
          revenue: rev
        };
      });

      return { data: result, error: null };
    } catch (err: any) {
      console.error('Erro ao buscar lista de integrações admin:', err);
      return { data: [], error: err };
    }
  },

  /**
   * Obtém a configuração de câmbio atual (COP -> BRL)
   */
  getCurrencyConfig: async (): Promise<{ cop_to_brl_rate: number; currency_origin: string; currency_target: string }> => {
    try {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', 'mastershop_currency_config')
        .maybeSingle();

      if (!error && data?.value?.cop_to_brl_rate) {
        return {
          cop_to_brl_rate: Number(data.value.cop_to_brl_rate),
          currency_origin: data.value.currency_origin || 'COP',
          currency_target: data.value.currency_target || 'BRL',
        };
      }
    } catch (e) {
      // Ignora e usa fallback
    }

    return {
      cop_to_brl_rate: 0.0016,
      currency_origin: 'COP',
      currency_target: 'BRL',
    };
  },

  /**
   * Salva uma nova taxa de conversão de câmbio (Admin)
   */
  saveCurrencyConfig: async (copToBrlRate: number): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase
        .from('platform_settings')
        .upsert({
          key: 'mastershop_currency_config',
          value: {
            cop_to_brl_rate: copToBrlRate,
            currency_origin: 'COP',
            currency_target: 'BRL',
            multiplier_label: `${(copToBrlRate * 100).toFixed(2)} por 100 COP`,
            updated_at: new Date().toISOString(),
          },
          updated_at: new Date().toISOString(),
        });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (e: any) {
      return { success: false, error: e.message || 'Erro ao salvar cotação.' };
    }
  }
};
